import React, { useState, useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Order from './Order';
import Payment from './Payment';
import EmployeeDashboardLayout from './EmployeeDashboardLayout';
import {
  applyStoreSessionFromUser,
  resolveEmployeeStoreContext,
} from './entityContext';
import {
  DEFAULT_DELIVERY_AGENT_ORDER_SOURCE,
  getDeliveryAgentOrderSources,
} from './menuChannels';
import { filterDeliveryAgentOrderSources } from './restaurantServiceAccess';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import { getDefaultPosTab, getSessionPermissions, hasAnyPage, resolveAllowedAdminTab } from './rbacSession';
import { ROUTES } from '../constants/appRoutes';
import './css/Home.css';
import './css/Employee.css';

const EMPLOYEE_SIDEBAR_ITEMS = [
  ['orders', 'Orders'],
  ['payment', 'Payment'],
];

const Employee = () => {
  const location = useLocation();
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });
  const userRole = user?.userRole || user?.role;

  const isDeliveryAgent = userRole === 'DELIVERY_AGENT';
  const isManager = userRole === 'MANAGER';
  const isStore = resolveEmployeeStoreContext(user);
  const { enabledTypes: enabledServiceTypes } = useRestaurantEnabledServices();

  const deliveryOrderSources = useMemo(() => {
    const sources = getDeliveryAgentOrderSources(isStore);
    return isStore ? sources : filterDeliveryAgentOrderSources(sources, enabledServiceTypes);
  }, [isStore, enabledServiceTypes]);

  const [activeTab, setActiveTab] = useState('orders');
  const [deliveryOrderSource, setDeliveryOrderSource] = useState(() => {
    const fromNav = location.state?.deliveryOrderSource;
    if (fromNav) return String(fromNav).toUpperCase();
    return DEFAULT_DELIVERY_AGENT_ORDER_SOURCE;
  });

  useEffect(() => {
    applyStoreSessionFromUser(user);
    const syncUser = () => {
      try {
        setUser(JSON.parse(localStorage.getItem('user') || 'null'));
      } catch {
        setUser(null);
      }
    };
    syncUser();
    const t = window.setTimeout(syncUser, 400);
    window.addEventListener('focus', syncUser);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('focus', syncUser);
    };
  }, [user?.userId, user?.user_id]);

  useEffect(() => {
    const fromNav = location.state?.deliveryOrderSource;
    if (!fromNav) return;
    const normalized = String(fromNav).toUpperCase();
    if (deliveryOrderSources.some((ch) => ch.key === normalized)) {
      setDeliveryOrderSource(normalized);
    }
  }, [location.state?.deliveryOrderSource, deliveryOrderSources]);

  useEffect(() => {
    if (isDeliveryAgent) {
      setActiveTab('orders');
    }
  }, [isDeliveryAgent]);

  useEffect(() => {
    if (!deliveryOrderSources.length) return;
    if (!deliveryOrderSources.some((ch) => ch.key === deliveryOrderSource)) {
      setDeliveryOrderSource(deliveryOrderSources[0].key);
    }
  }, [deliveryOrderSources, deliveryOrderSource]);

  const sessionPermissions = getSessionPermissions();
  if (sessionPermissions != null && hasAnyPage(sessionPermissions)) {
    const adminTab = resolveAllowedAdminTab(getDefaultPosTab());
    return <Navigate to={`${ROUTES.businessAdmin}?tab=${adminTab}`} replace />;
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'orders':
        return (
          <Order
            deliveryOrderSource={
              isDeliveryAgent ? deliveryOrderSource : undefined
            }
            enabledServiceTypes={enabledServiceTypes}
          />
        );
      case 'payment':
        return isManager ? <Payment /> : null;
      default:
        return null;
    }
  };

  if (isDeliveryAgent) {
    return (
      <EmployeeDashboardLayout
        deliveryOrderSource={deliveryOrderSource}
        deliveryOrderSources={deliveryOrderSources}
        onSelectOrderSource={setDeliveryOrderSource}
        onNavigateOrders={() => setActiveTab('orders')}
      >
        {renderTabContent()}
      </EmployeeDashboardLayout>
    );
  }

  const sidebarItems = isManager
    ? EMPLOYEE_SIDEBAR_ITEMS
    : EMPLOYEE_SIDEBAR_ITEMS.filter(([id]) => id === 'orders');

  return (
    <div className="restaurant-dashboard restaurant-dashboard--flush employee-dashboard">
      <aside className="restaurant-dashboard-sidebar" aria-label="Employee navigation">
        {sidebarItems.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`restaurant-dashboard-sidebar-item${
              activeTab === id ? ' active' : ''
            }`}
            onClick={() => setActiveTab(id)}
          >
            {label}
          </button>
        ))}
      </aside>
      <main className="restaurant-dashboard-main restaurant-dashboard-main--flush employee-dashboard-main">
        {renderTabContent()}
      </main>
    </div>
  );
};

export default Employee;
